/**
 * RIG live data — reads the deployed get_signal_relationships v7 edge function.
 *
 * This is a pure READ (supabase.functions.invoke), not a mutation: it does not
 * record an action event, so opening the RIG never pollutes the very action
 * history the surface displays. The function authenticates the user but is not
 * admin-gated, so any authenticated viewer can load a signal's relationship
 * graph. (The signal brief and action history reuse the existing read hooks.)
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/auth/supabase-client";
import { formatDayMonYear } from "./logic";
import type { SignalRelationshipResultV7 } from "./payload";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface FunctionsErrorLike {
  message: string;
  context?: { json?: () => Promise<unknown> };
}

async function messageFor(error: FunctionsErrorLike): Promise<string> {
  const ctx = error.context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const body = (await ctx.json()) as { error?: { message?: string }; message?: string };
      return body?.error?.message ?? body?.message ?? error.message;
    } catch {
      /* fall through to the generic message */
    }
  }
  return error.message;
}

export async function fetchSignalRelationships(
  signalId: string,
): Promise<SignalRelationshipResultV7> {
  const { data, error } = await supabase.functions.invoke<{
    data?: { result?: SignalRelationshipResultV7 };
  }>("get_signal_relationships", { body: { signal_id: signalId } });
  if (error) throw new Error(`get_signal_relationships: ${await messageFor(error as FunctionsErrorLike)}`);
  const result = data?.data?.result;
  if (!result) throw new Error("get_signal_relationships: missing relationship result payload");
  return result;
}

export function useRigRelationships(signalId: string | null) {
  return useQuery({
    queryKey: ["rig", "relationships", signalId],
    queryFn: () => fetchSignalRelationships(signalId as string),
    enabled: Boolean(signalId),
    staleTime: 60_000,
    retry: false,
  });
}

/* --- Contact-detail PII (get_contact_detail v1, owner/admin-gated) ---------- */

/** PII fields the RIG modal renders; provenance stays the modal's derived line. */
export interface ContactPiiFields {
  email: string;
  phone: string;
  linkedin: string;
  note: string;
  timeline: string[];
}

/** Discriminated outcome so 403 (denial → ownership state) is distinguished
 *  from 404 (not-found) and other errors, per the layered PII ruling. */
export type ContactDetailResult =
  | { status: "ok"; fields: ContactPiiFields }
  | { status: "forbidden" }
  | { status: "not_found" }
  | { status: "error"; message: string };

interface ContactDetailRow {
  email?: string | null;
  phone?: string | null;
  linkedin?: string | null;
  linkedin_url?: string | null;
  notes?: string | null;
  interaction_timeline?: { method?: string | null; occurred_at?: string | null }[] | null;
}

function mapPiiFields(row: ContactDetailRow): ContactPiiFields {
  const timeline = (row.interaction_timeline ?? [])
    .map((e) => {
      const date = e.occurred_at ? formatDayMonYear(e.occurred_at).toUpperCase() : "";
      const method = (e.method ?? "").toUpperCase().replace(/_/g, " ");
      return [date, method].filter(Boolean).join(" — ");
    })
    .filter(Boolean);
  return {
    email: row.email ?? "—",
    phone: row.phone ?? "—",
    linkedin: row.linkedin ?? row.linkedin_url ?? "—",
    note: row.notes ?? "",
    timeline,
  };
}

export async function fetchContactDetail(contactId: string): Promise<ContactDetailResult> {
  const { data, error } = await supabase.functions.invoke<{
    data?: { contact?: ContactDetailRow };
  }>("get_contact_detail", { body: { contact_id: contactId } });
  if (error) {
    const status = (error as { context?: { status?: number } }).context?.status;
    if (status === 403) return { status: "forbidden" };
    if (status === 404) return { status: "not_found" };
    return {
      status: "error",
      message: `get_contact_detail: ${await messageFor(error as FunctionsErrorLike)}`,
    };
  }
  const contact = data?.data?.contact;
  if (!contact) return { status: "error", message: "get_contact_detail: missing contact payload" };
  return { status: "ok", fields: mapPiiFields(contact) };
}

export function useContactDetail(contactId: string | null) {
  return useQuery<ContactDetailResult>({
    queryKey: ["rig", "contact-detail", contactId],
    queryFn: () => fetchContactDetail(contactId as string),
    enabled: Boolean(contactId) && UUID_RE.test(contactId ?? ""),
    staleTime: 60_000,
    retry: false,
  });
}
